import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../utils/formatters';
import axios from 'axios';
import { FiX, FiUpload, FiInfo, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import './ImportDataModal.css';

// CSV 가져오기 — DBeaver 의 "Import data" 에 대응.
// 3단계: ① 파일·옵션 → ② 대상 테이블 + 컬럼 매핑 → ③ 결과
//
// 이 기능은 DB 에 쓴다. 서버가 읽기전용 연결과 앱 권한 정책(기본 READ_ONLY)으로
// 이중 차단하며, 화면에서도 실행 직전에 대상과 행 수를 다시 보여준다.

const API_ROOT = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
const API_BASE = `${API_ROOT}/sql-developer`;

const getDelimiters = (t) => [
    { value: 'comma', label: t('dataTransfer:imp.comma') },
    { value: 'tab', label: t('dataTransfer:imp.tab') },
    { value: 'semicolon', label: t('dataTransfer:imp.semicolon') },
    { value: 'pipe', label: t('dataTransfer:imp.pipe') },
];

const getSteps = (t) => [t('dataTransfer:imp.pickFile'), t('dataTransfer:imp.targetMapping'), t('dataTransfer:imp.result')];

// CSV 컬럼명 ↔ DB 컬럼명 자동 매칭 — 대소문자/언더스코어 차이를 무시하고 맞춰본다.
const normalize = (s) => String(s || '').trim().toUpperCase().replace(/[\s_-]/g, '');

const ImportDataModal = ({ isOpen, onClose, connectionId, connectionName, onImported }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    const [file, setFile] = useState(null);
    const [delimiter, setDelimiter] = useState('comma');
    const [hasHeader, setHasHeader] = useState(true);
    const [encoding, setEncoding] = useState('utf8');
    const [nullText, setNullText] = useState('');
    const [batchSize, setBatchSize] = useState(500);
    const [stopOnError, setStopOnError] = useState(true);

    const [analysis, setAnalysis] = useState(null);   // { columns, preview, totalRows }
    const [schema, setSchema] = useState('');
    const [table, setTable] = useState('');
    const [targetColumns, setTargetColumns] = useState([]);
    const [mapping, setMapping] = useState([]);       // [{ csvColumn, dbColumn }]
    const [result, setResult] = useState(null);

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const reset = useCallback(() => {
        setStep(0); setFile(null); setAnalysis(null); setSchema(''); setTable('');
        setTargetColumns([]); setMapping([]); setResult(null); setError(null);
    }, []);

    const handleClose = () => { reset(); onClose(); };

    // ── ① 파일 분석 ─────────────────────────────────────────────────────────
    const handleAnalyze = async () => {
        if (!file) { setError(t('dataTransfer:imp.pickFileFirst')); return; }
        setBusy(true); setError(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('delimiter', delimiter);
            fd.append('hasHeader', String(hasHeader));
            fd.append('encoding', encoding);
            const res = await axios.post(`${API_BASE}/import/csv/analyze`, fd);
            setAnalysis(res.data);
            setMapping(res.data.columns.map(c => ({ csvColumn: c, dbColumn: '' })));
            setStep(1);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally { setBusy(false); }
    };

    // ── ② 대상 테이블 컬럼 로드 + 자동 매핑 ─────────────────────────────────
    const handleLoadTarget = async () => {
        if (!schema.trim() || !table.trim()) { setError(t('dataTransfer:imp.schemaTableRequired')); return; }
        setBusy(true); setError(null);
        try {
            const res = await axios.get(`${API_BASE}/import/target-columns`, {
                params: { connectionId, schema: schema.trim(), table: table.trim() },
            });
            const cols = res.data.columns || [];
            setTargetColumns(cols);
            // 이름이 맞는 것끼리 자동 연결 — 안 맞으면 비워 두고 사용자가 고른다.
            setMapping(prev => prev.map(m => {
                const hit = cols.find(c => normalize(c.name) === normalize(m.csvColumn));
                return { ...m, dbColumn: hit ? hit.name : '' };
            }));
        } catch (err) {
            setTargetColumns([]);
            setError(err.response?.data?.error || err.message);
        } finally { setBusy(false); }
    };

    // ── ③ 적재 ──────────────────────────────────────────────────────────────
    const handleImport = async () => {
        const mapped = mapping.filter(m => m.dbColumn);
        if (mapped.length === 0) { setError(t('dataTransfer:imp.mapAtLeastOne')); return; }
        setBusy(true); setError(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('connectionId', connectionId);
            fd.append('schema', schema.trim());
            fd.append('table', table.trim());
            fd.append('mapping', JSON.stringify(mapped));
            fd.append('options', JSON.stringify({
                delimiter, hasHeader, encoding, nullText, batchSize, stopOnError,
            }));
            const res = await axios.post(`${API_BASE}/import/csv/execute`, fd);
            setResult(res.data);
            setStep(2);
            if (onImported) onImported(res.data);
        } catch (err) {
            const d = err.response?.data;
            if (d && typeof d.inserted === 'number') { setResult({ ...d, failedAll: true }); setStep(2); }
            else setError(d?.error || err.message);
        } finally { setBusy(false); }
    };

    const mappedCount = mapping.filter(m => m.dbColumn).length;

    if (!isOpen) return null;

    return (
        <div className="import-modal-overlay" onClick={handleClose}>
            <div className="import-modal" onClick={e => e.stopPropagation()}>
                <div className="import-modal-header">
                    <h2><FiUpload size={16} /> {t('dataTransfer:imp.title')}</h2>
                    <button className="import-modal-close" onClick={handleClose}><FiX size={18} /></button>
                </div>

                <div className="import-steps">
                    {getSteps(t).map((s, i) => (
                        <div key={s} className={`import-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
                            <span className="import-step-no">{i + 1}</span>{s}
                        </div>
                    ))}
                </div>

                <div className="import-modal-body">
                    {/* ── ① 파일 선택 ── */}
                    {step === 0 && (
                        <div className="import-pane">
                            <label className="import-field">
                                <span className="import-label">{t('dataTransfer:imp.file')}</span>
                                <input type="file" accept=".csv,text/csv" onChange={e => setFile(e.target.files[0] || null)} />
                            </label>
                            <label className="import-field">
                                <span className="import-label">{t('dataTransfer:imp.delimiter')}</span>
                                <select value={delimiter} onChange={e => setDelimiter(e.target.value)}>
                                    {getDelimiters(t).map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                </select>
                            </label>
                            <label className="import-field">
                                <span className="import-label">{t('dataTransfer:imp.encoding')}</span>
                                <select value={encoding} onChange={e => setEncoding(e.target.value)}>
                                    <option value="utf8">UTF-8</option>
                                    <option value="euc-kr">EUC-KR / CP949</option>
                                </select>
                            </label>
                            <label className="import-field">
                                <span className="import-label">{t('dataTransfer:imp.nullText')}</span>
                                <input type="text" value={nullText} placeholder={t('dataTransfer:imp.blankAsNull')}
                                    onChange={e => setNullText(e.target.value)} />
                            </label>
                            <div className="import-checks">
                                <label>
                                    <input type="checkbox" checked={hasHeader} onChange={e => setHasHeader(e.target.checked)} />
                                    {t('dataTransfer:imp.firstRowHeader')}
                                </label>
                            </div>
                            <div className="import-note">
                                <FiInfo size={12} />
                                {t('dataTransfer:imp.rfcNote')}
                                {t('dataTransfer:imp.nullNote1')} <code>""</code> {t('dataTransfer:imp.nullNote2')}
                            </div>
                        </div>
                    )}

                    {/* ── ② 대상 · 매핑 ── */}
                    {step === 1 && analysis && (
                        <div className="import-pane">
                            <div className="import-summary">
                                <strong>{analysis.fileName}</strong>
                                <span>{t('dataTransfer:imp.summary', { cols: analysis.columns.length, rows: formatNumber(analysis.totalRows) })}</span>
                            </div>

                            <div className="import-target">
                                <label className="import-field">
                                    <span className="import-label">{t('dataTransfer:imp.schema')}</span>
                                    <input type="text" value={schema} placeholder={t('dataTransfer:imp.schemaPlaceholder')}
                                        onChange={e => setSchema(e.target.value)} />
                                </label>
                                <label className="import-field">
                                    <span className="import-label">{t('dataTransfer:imp.table')}</span>
                                    <input type="text" value={table} placeholder={t('dataTransfer:imp.tablePlaceholder')}
                                        onChange={e => setTable(e.target.value)} />
                                </label>
                                <button className="import-btn" onClick={handleLoadTarget} disabled={busy}>
                                    {t('dataTransfer:imp.loadColumns')}
                                </button>
                            </div>

                            {targetColumns.length > 0 && (
                                <>
                                    <div className="import-map-head">
                                        {t('dataTransfer:imp.columnMapping')} <span className="import-hint">{t('dataTransfer:imp.mappedCount', { n: mappedCount, total: mapping.length })}</span>
                                    </div>
                                    <div className="import-map-list">
                                        {mapping.map((m, i) => (
                                            <div key={m.csvColumn} className="import-map-row">
                                                <span className="import-csv-col" title={m.csvColumn}>{m.csvColumn}</span>
                                                <span className="import-arrow">→</span>
                                                <select
                                                    value={m.dbColumn}
                                                    onChange={e => setMapping(prev => prev.map((x, j) =>
                                                        j === i ? { ...x, dbColumn: e.target.value } : x))}
                                                >
                                                    <option value="">{t('dataTransfer:imp.excluded')}</option>
                                                    {targetColumns.map(c => (
                                                        <option key={c.name} value={c.name}>
                                                            {c.name} · {c.dataType}{c.nullable ? '' : ' *'}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {analysis.preview?.length > 0 && (
                                <details className="import-preview">
                                    <summary>{t('dataTransfer:imp.preview')}</summary>
                                    <div className="import-preview-scroll">
                                        <table>
                                            <thead><tr>{analysis.columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
                                            <tbody>
                                                {analysis.preview.map((r, i) => (
                                                    <tr key={i}>{r.map((v, j) => <td key={j}>{v}</td>)}</tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </details>
                            )}

                            <div className="import-field">
                                <span className="import-label">{t('dataTransfer:imp.batchSize')}</span>
                                <input type="number" min="1" max="5000" value={batchSize}
                                    onChange={e => setBatchSize(e.target.value)} />
                            </div>
                            <div className="import-checks">
                                <label>
                                    <input type="checkbox" checked={stopOnError} onChange={e => setStopOnError(e.target.checked)} />
                                    {t('dataTransfer:imp.rollbackLabel')} <span className="import-hint">{t('dataTransfer:imp.rollbackOff')}</span>
                                </label>
                            </div>

                            <div className="import-warn">
                                <FiAlertTriangle size={13} />
                                <span>
                                    {t('dataTransfer:imp.willInsert', { target: `${schema || '?'}.${table || '?'}`, rows: formatNumber(analysis.totalRows) })}
                                    {t('dataTransfer:imp.undoNote')}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* ── ③ 결과 ── */}
                    {step === 2 && result && (
                        <div className="import-pane">
                            <div className={`import-result ${result.failedAll ? 'fail' : 'ok'}`}>
                                {result.failedAll
                                    ? <><FiAlertTriangle size={16} /> {t('dataTransfer:imp.rolledBack')}</>
                                    : <><FiCheckCircle size={16} /> {t('dataTransfer:imp.done')}</>}
                            </div>
                            <div className="import-result-stats">
                                <div><span>{t('dataTransfer:imp.target')}</span><strong>{result.table || `${schema}.${table}`}</strong></div>
                                <div><span>{t('dataTransfer:imp.inserted')}</span><strong>{t('dataTransfer:imp.nRows', { n: formatNumber(result.inserted || 0) })}</strong></div>
                                <div><span>{t('dataTransfer:imp.failed')}</span><strong>{t('dataTransfer:imp.nRows', { n: formatNumber(result.failed || 0) })}</strong></div>
                                {result.elapsedMs != null && <div><span>{t('dataTransfer:imp.elapsed')}</span><strong>{t('dataTransfer:imp.nSeconds', { n: (result.elapsedMs / 1000).toFixed(2) })}</strong></div>}
                            </div>
                            {result.errors?.length > 0 && (
                                <details className="import-preview" open>
                                    <summary>{t('dataTransfer:imp.errorDetail')}</summary>
                                    <div className="import-preview-scroll">
                                        <table>
                                            <thead><tr><th>{t('dataTransfer:imp.fileLine')}</th><th>{t('dataTransfer:imp.message')}</th></tr></thead>
                                            <tbody>
                                                {result.errors.map((e, i) => (
                                                    <tr key={i}><td>{e.line}</td><td>{e.message}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </details>
                            )}
                        </div>
                    )}

                    {error && <div className="import-error">{error}</div>}
                </div>

                <div className="import-modal-footer">
                    {step === 1 && <button className="import-btn" onClick={() => setStep(0)} disabled={busy}>{t('dataTransfer:imp.prev')}</button>}
                    <div className="import-footer-spacer" />
                    <button className="import-btn" onClick={handleClose}>{step === 2 ? t('dataTransfer:imp.close') : t('dataTransfer:imp.cancel')}</button>
                    {step === 0 && (
                        <button className="import-btn import-btn-primary" onClick={handleAnalyze} disabled={busy || !file}>
                            {busy ? t('dataTransfer:imp.analyzing') : t('dataTransfer:imp.next')}
                        </button>
                    )}
                    {step === 1 && (
                        <button className="import-btn import-btn-danger" onClick={handleImport}
                            disabled={busy || mappedCount === 0}>
                            <FiUpload size={13} /> {busy ? t('dataTransfer:imp.loading') : t('dataTransfer:imp.run')}
                        </button>
                    )}
                    {step === 2 && (
                        <button className="import-btn" onClick={reset}>{t('dataTransfer:imp.again')}</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportDataModal;
